export const ABOUT_US_SCHEMA = {
  en: {
    title: "The Plaintr Standard",
    sub_title:
      "Helping people make sense of their financial lives starts with a simple belief. Understanding should never be harder than the decision itself.",
    hero_ctas: [
      { text: "Get Started", link: "/sign-up", type: "primary_light" },
      { text: "Sign In", link: "/sign-in", type: "secondary" },
    ],
    why_exists: {
      heading: "When Things Make Sense.",
      content: [
        "Financial confidence doesn't begin with more information. It begins with understanding.",
        "Every day, we make financial decisions.",
        "Some are small enough to forget by tomorrow. Others quietly shape where we'll be years from now. Whether we're filing taxes, comparing options, or simply trying to understand where our money is going, those decisions are all part of something much bigger: the financial lives we're building every day.",
        "We've never had more access to financial information, yet making financial decisions hasn't necessarily become easier. Somewhere along the way, we started assuming that if information became more accessible, understanding would naturally follow.",
        "We don't believe that's true.",
        "Information can tell us what happened, but understanding helps us decide what comes next. That's the difference between looking at numbers and recognizing a pattern, between reviewing options and knowing which one fits your life, or between having answers and knowing what to do with them.",
        "Everything we've built begins there.",
        "We believe financial confidence starts with understanding—not because people need more dashboards, more notifications, or more complexity wrapped in a modern interface, but because they deserve to understand the financial decisions shaping their lives.",
      ],
    },
    not_for_everyone: {
      heading: "This Is What That Looks Like.",
      cards: [
        {
          heading: "You shouldn't need to speak tax.",
          image: "/images/pricing_that_fit.svg",
          image_alt: "You shouldn't need to speak tax.",
          content: [
            "Taxes affect real people, real decisions, and real outcomes. Understanding your options shouldn't depend on decoding complex language or navigating the process alone. That's why we connect people with certified professionals who provide clarity, guidance, and confidence when it matters most.",
          ],
        },
        {
          heading: "Money doesn't come with explanations.",
          image: "/images/fi_about_us.svg",
          image_alt: "Money doesn't come with explanations.",
          content: [
            "Every transaction tells part of the story, but very few explain the bigger picture. The patterns shaping our financial lives rarely appear all at once. They emerge over time, across everyday decisions that seem unrelated at first. Financial Insights was built to bring those patterns into focus, turning everyday financial activity into something easier to understand.",
          ],
        },
      ],
    },
    closing_sentence: {
      heading: "This Is What We Believe.",
      content: [
        "Plaintr isn't built around taxes, budgeting, or any single financial tool. We're building a company dedicated to helping people understand the financial decisions shaping their lives.",
        "That's why Tax Filing exists. That's why Financial Insights exists.",
        "And it's why every product we build in the future will begin with the same question: Will this help someone understand something that matters?",
      ],
      closing:
        "We started by translating tax code into human language. Today, we're translating financial complexity into human understanding..",
    },
    section_1: {
      p_content_1: [
        "Plaintr wasn’t built to be “another tax platform.” It was built because the tax world forgot who it was supposed to serve — people.",
        "For too long, filing has been cold and robotic — designed around the process, not the person. Complex rules. Confusing forms. Generic answers. All delivered in a language that makes you want to stop listening.",
        {
          text: "We started with a different question: What if taxes felt human?",
          highlights: ["What if taxes felt human?"],
        },
        "We believe in real help from real experts.",
        "We believe your life — your work, your family, your side hustle, your business — should shape how you file.",
        "And we believe the price you see should be the price you pay. Every time.",
      ],
    },
    section_2: {
      intro: "Not for everyone. For you.",
      p_content_1: [
        "We work only with CPAs, Enrolled Agents, or Tax Attorneys — vetted, certified, and validated internally before they ever take on a client. These are the same top-tier professionals high-net-worth individuals and private firms rely on — now made accessible without compromising quality.",
        "Our Smart Match intake covers every tax situation, from multiple-state filings to complex investments. Based on your answers, we hand-pick the exact type of pro your case demands — not just whoever is available.",
        "Your fee is built for your life, not for a generic tax category. Once it’s set, it stays that way — because we take the time upfront to understand your full situation.",
        "We don’t start free and finish expensive; we start fair and finish strong. While other platforms lure you in with “estimates” that can climb once you’re already invested, we remove that trap entirely. The number you approve at the start is the one you finish with — giving you the clarity, control, and peace of mind that’s almost impossible to find elsewhere.",
      ],
    },
    section_3: {
      intro: "This isn’t just about filing taxes.",
      p_content_1: [
        "It’s about joining a service where every pro you meet is at the top of their field, matched to you personally, and invested in your best outcome.",
        "It’s about replacing uncertainty with clarity — and replacing stress with trust.",
        "Your life. Your numbers. Your peace of mind.",
      ],
      p_content_closing: ["That’s Plaintr."],
    },
    cta_suggestions: {
      intro: "Let’s file, the right way",
      links: [
        { text: "Sign In", link: "/sign-in", stl: "w" },
        { text: "Create Account", link: "/sign-up", stl: "w" },
        { text: "How We Match You", link: "/how-it-works", stl: "b" },
      ],
    },
  },
};
